import { motion, useReducedMotion } from 'motion/react';
import { useId, useLayoutEffect, useRef, useState } from 'react';
import type { ComponentPropsWithRef, KeyboardEvent, ReactNode } from 'react';

import { cn } from '../../lib/cn';
import { PRESS } from '../../interaction';

export type SegmentedSize = 'sm' | 'md' | 'lg';

export interface SegmentedOption {
  value: string;
  label: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
}

export interface SegmentedControlProps
  extends Omit<ComponentPropsWithRef<'div'>, 'children' | 'onChange'> {
  options: SegmentedOption[];
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  size?: SegmentedSize;
  /** Accessible name for the radiogroup. */
  label?: string;
  /** Stretches the control and its segments to the container. */
  fit?: boolean;
}

/**
 * Every segment carries a `min-w`, and the track is never `w-full` on its own.
 * That is the §15 gotcha's fix: in an auto-width box a `flex-1` child with a
 * `basis-0` collapses to roughly nothing, which is how a segmented control ends
 * up rendering as one unclickable sliver.
 */
const SEGMENTS: Record<SegmentedSize, string> = {
  // The `max-sm:` half of each entry releases the minimum and tightens the
  // padding below 640px. Three `md` segments at their floor are 216px of
  // segment plus 16px of gap and 8px of track padding = 240px, in a container
  // that is 230px wide at a 320px viewport — which made the whole document
  // scroll sideways (measured: `documentElement.scrollWidth` 350 against 320,
  // WCAG 1.4.10 Reflow). Released, the same three segments fit in 193px.
  sm: 'h-7 min-w-[3.5rem] gap-1.5 px-2.5 text-2xs max-sm:min-w-0 max-sm:gap-1 max-sm:px-2',
  md: 'h-9 min-w-[4.5rem] gap-2 px-3.5 text-xs max-sm:min-w-0 max-sm:gap-1 max-sm:px-2',
  lg: 'h-11 min-w-[5.5rem] gap-2 px-4 text-sm max-sm:min-w-0 max-sm:gap-1 max-sm:px-2',
};

interface ThumbRect {
  x: number;
  width: number;
}

export function SegmentedControl({
  options,
  value,
  defaultValue,
  onValueChange,
  size = 'md',
  label,
  fit = false,
  className,
  ...rest
}: SegmentedControlProps) {
  const reduceMotion = useReducedMotion();
  const baseId = useId();
  const trackRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const [uncontrolled, setUncontrolled] = useState(defaultValue ?? options[0]?.value ?? '');
  const selected = value ?? uncontrolled;
  const selectedIndex = options.findIndex((option) => option.value === selected);
  const [thumb, setThumb] = useState<ThumbRect | null>(null);

  useLayoutEffect(() => {
    const option = selectedIndex >= 0 ? optionRefs.current[selectedIndex] : undefined;
    if (!option) {
      setThumb(null);
      return;
    }
    const measure = () => setThumb({ x: option.offsetLeft, width: option.offsetWidth });
    measure();

    // Absent under jsdom, so it is probed rather than assumed.
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(option);
    if (trackRef.current) observer.observe(trackRef.current);
    return () => observer.disconnect();
  }, [selectedIndex, size, fit]);

  function choose(option: SegmentedOption) {
    if (option.disabled || option.value === selected) return;
    if (value === undefined) setUncontrolled(option.value);
    onValueChange?.(option.value);
  }

  // A radiogroup moves *and* selects with the arrows, and skips disabled
  // options rather than parking on them.
  function moveTo(from: number, step: 1 | -1) {
    const enabled = options.map((option, index) => (option.disabled ? -1 : index)).filter((i) => i >= 0);
    if (enabled.length === 0) return;

    const position = enabled.indexOf(from);
    const nextPosition =
      position === -1
        ? 0
        : (position + step + enabled.length) % enabled.length;
    const next = enabled[nextPosition];
    if (next === undefined) return;

    const option = options[next];
    if (!option) return;
    optionRefs.current[next]?.focus();
    choose(option);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        moveTo(selectedIndex, 1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        moveTo(selectedIndex, -1);
        break;
      case 'Home': {
        event.preventDefault();
        const first = options.findIndex((option) => !option.disabled);
        const option = first >= 0 ? options[first] : undefined;
        if (option) {
          optionRefs.current[first]?.focus();
          choose(option);
        }
        break;
      }
      case 'End': {
        event.preventDefault();
        const last = options.reduce((acc, option, index) => (option.disabled ? acc : index), -1);
        const option = last >= 0 ? options[last] : undefined;
        if (option) {
          optionRefs.current[last]?.focus();
          choose(option);
        }
        break;
      }
      default:
        break;
    }
  }

  // Exactly one segment sits in the tab order: the selected one, or the first
  // enabled one when nothing is selected.
  const firstEnabled = options.findIndex((option) => !option.disabled);
  const tabbableIndex = selectedIndex >= 0 ? selectedIndex : firstEnabled;

  return (
    <div
      {...rest}
      ref={trackRef}
      role="radiogroup"
      aria-label={label}
      onKeyDown={handleKeyDown}
      className={cn(
        'relative inline-flex min-w-0 max-w-full items-center gap-1 rounded-xs border border-line-subtle bg-surface-muted p-1',
        fit && 'w-full',
        className,
      )}
    >
      <motion.span
        aria-hidden="true"
        initial={false}
        animate={
          thumb
            ? { x: thumb.x, width: thumb.width, opacity: 1 }
            : { opacity: 0 }
        }
        transition={
          // MotionConfig's `reducedMotion="user"` strips the transform but would
          // still tween the width, so the whole tween is collapsed here.
          reduceMotion
            ? { duration: 0 }
            : { type: 'spring', stiffness: 560, damping: 42 }
        }
        className="pointer-events-none absolute inset-y-1 left-0 rounded-xs border border-line-strong bg-surface"
      />

      {options.map((option, index) => {
        const isSelected = index === selectedIndex;
        return (
          <button
            key={option.value}
            ref={(node) => {
              optionRefs.current[index] = node;
            }}
            type="button"
            role="radio"
            id={`${baseId}-option-${option.value}`}
            aria-checked={isSelected}
            aria-disabled={option.disabled || undefined}
            tabIndex={index === tabbableIndex ? 0 : -1}
            onClick={() => choose(option)}
            className={cn(
              'dhd-target relative z-10 inline-flex items-center justify-center rounded-xs',
              'font-display font-semibold uppercase tracking-label whitespace-nowrap',
              'transition-colors duration-[var(--dur-fast)] ease-[var(--ease-spring)]',
              'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
              SEGMENTS[size],
              fit && 'min-w-0 flex-1',
              isSelected ? 'text-heading' : 'text-muted hover:text-body',
              option.disabled && 'cursor-not-allowed opacity-40',
              !option.disabled && PRESS,
            )}
          >
            {option.icon ? (
              <span aria-hidden="true" className="grid size-4 shrink-0 place-items-center">
                {option.icon}
              </span>
            ) : null}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
